import React, { MouseEventHandler, useCallback } from 'react';
import { AspectRatio } from 'react-aspect-ratio';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  ButtonProps,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  closestCorners,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSwappingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import LazyAvatar from '../../../components/Avatar/LazyAvatar/LazyAvatar';
import { useConfirm } from '../../../components/Dialogs/Confirm/context/ConfirmDialog.context';
import { Item } from '../../../components/Photoswipe/Photoswipe';
import {
  PortfolioPhoto,
  ValidationError as PortfolioValidationError,
} from '../../../components/PortfolioPhoto/PortfolioPhoto';
import { environment } from '../../../config';
import { VenuePortfolioItem } from '../../../graphql/API';
import Icons from '../../../themes/icons';
import usePortfolioQuery from './usePortfolioQuery';
import { useUpdatePortfolioMutation } from './useUpdatePortfolioMutation';

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

type DeleteButtonProps = {
  isLoading?: boolean;
} & ButtonProps;

const DeleteButton: React.FC<DeleteButtonProps> = ({ isLoading, ...restProps }) => (
  <IconButton
    aria-label='delete'
    sx={(theme) => ({
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: theme.spacing(0.5),
      '.MuiCircularProgress-root': {
        color: 'white',
      },
      '.MuiSvgIcon-root': {
        color: 'white',
      },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
      },
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
      },
    })}
    disabled={isLoading}
    {...restProps}
  >
    {isLoading ? <CircularProgress size={24} /> : <DeleteIcon />}
  </IconButton>
);

const SortablePhotoItem: React.FC<{
  isDeleting: boolean;
  photo: VenuePortfolioItem;
  onDelete: React.MouseEventHandler<HTMLButtonElement>;
}> = ({ photo, onDelete, isDeleting }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging, transition, active } =
    useSortable({
      id: String(photo.id),
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: active ? 'grabbing' : 'grab',
  };

  return (
    <Box
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      style={style}
      sx={{
        transition: 'transform 0.25s ease',
        '&:hover': {
          transform: isDragging ? undefined : 'scale(1.02)',
        },
      }}
    >
      <AspectRatio ratio={1}>
        <Item itemProps={{ original: String(photo.asset?.large) }} key={photo.id}>
          <LazyAvatar
            variant='square'
            alt={String(photo.id)}
            placeholderSrc={String(photo.asset?.thumbnail)}
            src={String(photo.asset?.medium)}
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 1,
              ...(!photo.id ? { opacity: '50%' } : undefined),
            }}
            draggable={false}
          />
        </Item>

        {photo.id && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 2,
            }}
          >
            <DeleteButton value={String(photo.id)} onClick={onDelete} isLoading={isDeleting} />
          </Box>
        )}
      </AspectRatio>
    </Box>
  );
};

const PortfolioPanel: React.FC = () => {
  const { data, isLoading, deleteMutation } = usePortfolioQuery();
  const { mutateAsync: portfolioMutateAsync } = useUpdatePortfolioMutation();
  const { confirm } = useConfirm();

  const [photos, setPhotos] = React.useState<VenuePortfolioItem[]>(data);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [validationError, setValidationError] = React.useState<PortfolioValidationError | null>(
    null,
  );
  const [errorSourceIndex, setErrorSourceIndex] = React.useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(TouchSensor),
    useSensor(MouseSensor),
  );

  const measuringConfig = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  const dropAnimation = {
    ...defaultDropAnimation,
    dragSourceOpacity: 0.5,
  };

  React.useEffect(() => {
    if (data.length > 0) {
      setPhotos(data);
    }
  }, [data]);

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      e.stopPropagation();
      const id = e.currentTarget.value;

      const isConfirm = await confirm({
        title: 'Delete photo',
        confirmationText: 'Delete photo',
        description: <>Are you sure you want to delete the photo?</>,
      });

      if (isConfirm) {
        deleteMutation.mutate(id);
      }
    },
    [confirm, deleteMutation],
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveId(null);

      if (!over || active.id === over.id) return;

      const originalPosition = photos.findIndex((obj) => obj.id === active.id);
      const latestPosition = photos.findIndex((obj) => obj.id === over.id);

      const _photos = arrayMove(photos, originalPosition, latestPosition);
      setPhotos(_photos);

      try {
        await portfolioMutateAsync({
          items: _photos.map((item, index) => ({
            id: String(item.id),
            displayOrder: index,
          })),
        });
      } catch (error) {
        console.error(error);
        // Handle error
      }
    },
    [photos, portfolioMutateAsync],
  );

  const addBoxesLength = 10 - (photos?.length || 0);
  const activePhoto = activeId ? photos.find((photo) => photo.id === activeId) : null;

  return (
    <Box>
      <Box display={'flex'} flexDirection={'column'} gap='10px' mb={2}>
        <Typography fontWeight='bold'>
          Your photos
          <Box component='span' ml={2}>
            {photos.length ?? 0}/10
          </Box>
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#F4F4F4',
            border: '1px solid #EAEAEA',
            borderRadius: '8px',
            px: 1.5,
            py: 1,
            width: 'fit-content',
          }}
        >
          <Icons.InfoLightIcon />
          <Box display={'flex'} alignItems={'center'} gap={'10px'}>
            <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#848484' }}>
              Recommended size: 2000x2000px
            </Typography>
            <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#848484' }}>
              Min. 100KB - Max 5MB
            </Typography>
          </Box>
        </Box>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        measuring={measuringConfig}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={photos.map((photo) => String(photo.id))}
          strategy={rectSwappingStrategy}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
            {isLoading
              ? [...Array(3)].map((_, i) => (
                  <Box>
                    <AspectRatio>
                      <Skeleton variant='rectangular' />
                    </AspectRatio>
                  </Box>
                ))
              : photos.map((x) => (
                  <SortablePhotoItem
                    key={x.id}
                    photo={x}
                    onDelete={handleClick}
                    isDeleting={deleteMutation.variables === x.id && deleteMutation.isPending}
                  />
                ))}
            {!isLoading &&
              [...Array(addBoxesLength)].map((_, i) => (
                <AspectRatio key={i}>
                  <PortfolioPhoto
                    slotIndex={i}
                    hasError={validationError !== null && errorSourceIndex === i}
                    onValidationError={(err, idx) => {
                      setValidationError(err);
                      setErrorSourceIndex(err !== null ? idx : null);
                    }}
                  />
                </AspectRatio>
              ))}
          </Box>
        </SortableContext>

        <DragOverlay dropAnimation={dropAnimation}>
          {activePhoto ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: 3,
                transform: 'scale(1.05)',
                opacity: 0.9,
              }}
            >
              <LazyAvatar
                variant='square'
                alt={String(activePhoto.id)}
                src={String(activePhoto.asset?.medium)}
                sx={{ width: '100%', height: '100%' }}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Box px={1.5} pt={4.8} display={'flex'} alignItems={'center'} gap={'8px'}>
        <Icons.DragHandIcon />
        <Typography fontSize={'14px'} fontFamily={'Poppins'}>
          Hold and drag to rearrange the photos order
        </Typography>
      </Box>

      {validationError && (
        <Alert
          severity={validationError.severity}
          onClose={() => {
            setValidationError(null);
            setErrorSourceIndex(null);
          }}
          sx={{
            mt: 2,
            width: 'fit-content',
            backgroundColor: '#FDEDED',
            borderRadius: 1,
            color: '#BF3D3D',
            '& .MuiAlert-icon': { color: '#BF3D3D' },
            '& .MuiAlert-message': { color: '#BF3D3D', fontSize: '14px' },
            '& .MuiIconButton-root': { color: '#BF3D3D' },
          }}
        >
          {validationError.message}
        </Alert>
      )}
    </Box>
  );
};

export default PortfolioPanel;
